import React, { useState, useEffect, useRef } from "react";
import { Table } from "react-bootstrap";

import { getBackend as getBackend2 } from "@/lib/backend/Backend2";
import { niceDateTime, createDurationString } from "@/lib/Util";
import * as t from "@/lib/backend/common.generated.types.js";
import { translate } from "@/components/IntlProviderWrapper";
import Loader from "@/components/Loader";
import ErrorComponent from "@/components/ErrorComponent";

type InitialState = {
    data: t.IKeyValue[] | null;
};

const initialState: InitialState = {
    data: null,
};

const ModuleStatus = () => {
    const [state, setState] = useState(initialState);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const loadCancelledRef = useRef(false);

    useEffect(() => {
        loadCancelledRef.current = false;

        const load = async () => {
            setLoading(true);
            setError(null);
            try {
                const res = await getBackend2().common.searchKeyValues({ prefix: "module-status" });
                if (!loadCancelledRef.current) {
                    setState({ data: res.hits });
                }
            } catch (err) {
                if (!loadCancelledRef.current) {
                    setError((err as Error).message);
                }
            } finally {
                if (!loadCancelledRef.current) {
                    setLoading(false);
                }
            }
        };

        void load();

        return () => {
            loadCancelledRef.current = true;
        };
    }, []);

    const renderContent = (data: t.IKeyValue[]): React.ReactNode => {
        if (data.length === 0) {
            return translate("common.no_data");
        }

        return (
            <Table className="dashboard-table mb-0">
                <thead className="table-header-neutral">
                    <tr>
                        <th>{translate("common.module", "Module")}</th>
                        <th className="text-right">{translate("common.time", "Time")}</th>
                        <th className="text-center">{translate("common.status", "Status")}</th>
                    </tr>
                </thead>
                <tbody>
                    {data.map((el, i) => {
                        const { module, status, ts_epoch } = el.data;

                        return (
                            <tr key={i}>
                                <td className="text-monospace">{module}</td>
                                <td className="text-right">
                                    <span className="text-muted mr-2">
                                        {createDurationString(Date.now() - ts_epoch)} ago
                                    </span>
                                    {niceDateTime(new Date(ts_epoch))}
                                </td>
                                <td className="text-center text-uppercase">{status}</td>
                            </tr>
                        );
                    })}
                </tbody>
            </Table>
        );
    };

    return (
        <section>
            <h2 className="content-subtitle"> {translate("common.module_status", "Module status")}</h2>
            <div className="card p-0">
                <div className="card-content">
                    {error && <ErrorComponent msg={error} type="error" />}
                    {loading && <Loader small={true}></Loader>}
                    {state.data && renderContent(state.data)}
                </div>
            </div>
        </section>
    );
};

export default ModuleStatus;
